import { FC, useEffect, useState } from 'react'
import { enqueueSnackbar } from 'notistack'

import { ContributionPointBaseObject } from '@img-arena/consoles-api-streaming'
import { streamingRestApi } from '@/service/streamingApi'
import { PropertyTournament } from '@/types/streams/propertyTournament'

import { SelectBox } from '../SelectBox'

export const PROPERTY_TOURNAMENT_SELECT_TEST_ID = 'property-tournament-select'
export const FOLLOW_PROPERTY_LABEL = '(Follow property)'
export const TOURNAMENT_LABEL = 'Tournament'

type PropertyTournamentSelectProps = {
  property: ContributionPointBaseObject | null
  tournament: ContributionPointBaseObject | null
  onChange: (value: ContributionPointBaseObject | null) => void
  onClear: () => void
  label?: string
  testId?: string
  disabled?: boolean
  required?: boolean
  readOnly?: boolean
}
export const PropertyTournamentSelect: FC<PropertyTournamentSelectProps> = ({
  property,
  tournament,
  onChange,
  onClear,
  label,
  testId = PROPERTY_TOURNAMENT_SELECT_TEST_ID,
  disabled,
  required = false,
  readOnly
}) => {
  const sportsApi = streamingRestApi.endpoints.sports
  const [fetchedTournaments, setFetchedTournaments] = useState<PropertyTournament[]>([])

  useEffect(() => {
    if (readOnly) {
      return
    }

    if (!property) {
      setFetchedTournaments([])
      return
    }

    const ac = new AbortController()

    sportsApi
      .getPropertyTournaments({ id: property.id }, ac.signal)
      .then((tournaments) => {
        setFetchedTournaments(tournaments)
      })
      .catch((err) => {
        if (err.name === 'CanceledError') {
          return
        }
        enqueueSnackbar('Error fetching tournaments: ' + String(err), {
          variant: 'error'
        })
      })

    return () => {
      ac.abort()
    }
  }, [property])

  const computedLabel =
    label || (property && !tournament ? FOLLOW_PROPERTY_LABEL : TOURNAMENT_LABEL)

  if (readOnly) {
    return (
      <SelectBox
        testId={testId}
        value={tournament}
        getOptionId={(option) => String(option?.id)}
        formatListOption={(option) => option?.name}
        formatInputOption={(option) => option?.name}
        clearable
        disabled
        label={computedLabel}
        options={[tournament]}
        required={required}
        fullWidth
      />
    )
  }

  return (
    <SelectBox
      testId={testId}
      value={tournament}
      getOptionId={(option) => String(option.id)}
      formatListOption={(option) => option.name}
      formatInputOption={(option) => option.name}
      onChange={onChange}
      onClear={onClear}
      clearable
      disabled={!property || disabled}
      label={computedLabel}
      options={fetchedTournaments}
      required={required}
      fullWidth
    />
  )
}
